import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

export const fetchTrackableUsers = createAsyncThunk(
  "tracking/getUsers",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/gps-track", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch trackable users",
      );
    }
  },
);

export const fetchGpsStatus = createAsyncThunk(
  "tracking/getStatus",
  async ({ userId, params }, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/gps-track/${userId}`, { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch GPS status",
      );
    }
  },
);

export const fetchTrackingHistory = createAsyncThunk(
  "tracking/getHistory",
  async ({ userId, params }, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/gps-track/${userId}/history`, {
        params,
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch tracking history",
      );
    }
  },
);

// Tracking route views: the team's day with routes, and one person's route of the day
export const fetchTeamRoutes = createAsyncThunk(
  "tracking/getTeamRoutes",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/gps-track/team-routes", { params });
      if (res.data?.success === false) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch team routes");
    }
  },
);

export const fetchUserRoute = createAsyncThunk(
  "tracking/getUserRoute",
  async ({ userId, params }, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/gps-track/${userId}/route`, { params });
      if (res.data?.success === false) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch route");
    }
  },
);

const trackingSlice = createSlice({
  name: "tracking",
  initialState: {
    users: [],
    user: null,
    history: [],
    latest: null,
    is_online: false,
    total_records: 0,
    isLoading: false,
    error: null,
    // Website route views (separate keys, so the Monitoring screens using `users` / `history` are untouched)
    teamRoutes: [],
    teamRoutesLoading: false,
    route: null, // { date, summary, points, stops, trips, gaps }
    routeLoading: false,
  },
  reducers: {
    clearTrackingDetails: (state) => {
      state.user = null;
      state.history = [];
      state.latest = null;
      state.is_online = false;
      state.route = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamRoutes.pending, (state) => {
        state.teamRoutesLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamRoutes.fulfilled, (state, action) => {
        state.teamRoutesLoading = false;
        state.teamRoutes = action.payload?.data || [];
      })
      .addCase(fetchTeamRoutes.rejected, (state, action) => {
        state.teamRoutesLoading = false;
        state.error = action.payload || "Failed to fetch team routes";
      })

      .addCase(fetchUserRoute.pending, (state) => {
        state.routeLoading = true;
      })
      .addCase(fetchUserRoute.fulfilled, (state, action) => {
        state.routeLoading = false;
        state.route = action.payload;
      })
      .addCase(fetchUserRoute.rejected, (state, action) => {
        state.routeLoading = false;
        state.route = null;
        state.error = action.payload || "Failed to fetch route";
      })

      .addCase(fetchGpsStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGpsStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.latest = action.payload.latest_location;
        state.is_online = action.payload.is_online;
      })
      .addCase(fetchGpsStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch GPS status";
      })

      .addCase(fetchTrackableUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrackableUsers.fulfilled, (state, action) => {
        const payload = action.payload;
        const records = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

        state.isLoading = false;
        state.users = records;
        state.total_records = payload?.total_records || records.length;
      })
      .addCase(fetchTrackableUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch users";
      })

      .addCase(fetchTrackingHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrackingHistory.fulfilled, (state, action) => {
        const payload = action.payload;
        const records = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

        state.isLoading = false;
        state.history = records;
        state.total_records = payload?.total_records || records.length;
      })
      .addCase(fetchTrackingHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch tracking history";
      });
  },
});

export const { clearTrackingDetails } = trackingSlice.actions;
export default trackingSlice.reducer;
